/*
 * slider
 * modules.js: 
 */

import Swiper, { A11y, Navigation, Pagination, EffectFade, Autoplay } from 'swiper';
import defaultConfig from './sliderConfig.json';

window.swiper = [];

const slider = {
    cfg: {
        name: 'slider',
        selectors: {
            root: '.js-slider',
            controls: {
                container: '.js-sliderControls',
                next: '.swiper-next',
                prev: '.swiper-prev',
            },
            parentControls: {
                next: '.swiper-next-parent',
                prev: '.swiper-prev-parent',
            },
            indexControl: '.js-indexControl',
            pagination: '.swiper-pagination',
            container: '.swiper-container',
            fraction: '.js-sliderFraction',
            slide: '.js-slide',
            progress: '.js-sliderProgress',
            focusFix: '.js-sliderFocusFix',
        },
        classes: {
            isDisabled: 'is-disabled',
            isHidden: 'is-hidden',
            hasDesktopColValue: 'has-desktop-col-value-',
            textPagination: {
                bulletClass: 'swiper-pagination-title',
                bulletActiveClass: 'is-active',
            },
        },
        el: {
            $sliders: undefined,
        },
    },

    setElements(container = document) {
        this.cfg.el.$sliders = [...container.querySelectorAll(this.cfg.selectors.root)];
    },

    // utils
    validateMediaQuery(_el) {
        const validSelectors = [
            'min-width',
            'max-width',
        ];

        const regexPattern = validSelectors.map(key => `${key.replace(/([A-Z])/g, '-$1').toLowerCase()}\\s*:`).join('|');
        const mqSelector = new RegExp(`\\b(${regexPattern})`);

        const validSelector = mqSelector.test(_el);
        if (!validSelector) return console.warn(`Media Query ${_el} not valid. Use: ${validSelectors} instead.`);

        const validSelectorValue = _el.match(/:\s*(\d+)/);
        if (!validSelectorValue) return console.warn(`Please check if you have entered a number.`);
    },

    // initSliders
    initSliders() {
        this.cfg.el.$sliders.forEach((slider) => {
            const config = Object.assign({}, defaultConfig);
            const modules = [];
            const containerEl = slider.querySelector(this.cfg.selectors.container);

            if (!containerEl) return console.warn('Can not find swiper-container');
            const getSwiperInstance = () => slider.querySelector('.swiper-container-initialized').swiper;

            // controls
            // The "parentControls prop" is particularly useful when dealing 
            // with sliders containing child sliders. By setting pros.parentControls 
            // to true in the control.twig include, it allows efficient handling 
            // of navigation controls within the Swiper configuration for the parent.

            let controls;
            const parentControlPrev = slider.querySelector(this.cfg.selectors.parentControls.prev);
            const parentControlNext = slider.querySelector(this.cfg.selectors.parentControls.next);

            if (parentControlPrev && parentControlNext) {
                controls = { prev: parentControlPrev, next: parentControlNext };
            } else {
                controls = {
                    prev: slider.querySelector(this.cfg.selectors.controls.prev),
                    next: slider.querySelector(this.cfg.selectors.controls.next)
                };
            }

            if (controls.prev && controls.next) {
                config.navigation = {
                    nextEl: controls.next,
                    prevEl: controls.prev,
                    hiddenClass: this.cfg.classes.isHidden,
                    disabledClass: this.cfg.classes.isDisabled,
                }

                modules.push(Navigation);
            }

            // pagintaion
            const pagination = {
                el: slider.querySelector(this.cfg.selectors.pagination),
            }

            if (pagination.el) {
                config.pagination = {
                    el: pagination.el,
                    type: 'bullets',
                    clickable: true,
                    bulletElement: 'button',
                }

                modules.push(Pagination);
            }

            // fraction Text
            const textPagination = slider.getAttribute('swiper-text-pagination');
            if (textPagination != null) {
                config.pagination = {
                    el: slider.querySelector(this.cfg.selectors.pagination),
                    renderBullet: function (index, className) {
                        return `<button class="${className}">${slider.querySelector(`[data-swiper-slide-index="${index}"]`).getAttribute('data-swiper-title')}</button>`;
                    },
                    bulletClass: `${this.cfg.classes.textPagination.bulletClass}`,
                    bulletActiveClass: `${this.cfg.classes.textPagination.bulletActiveClass}`,
                    clickable: true,
                }
            }

            // fraction
            const fraction = {
                el: slider.querySelector(this.cfg.selectors.fraction),
                sep: slider.getAttribute('swiper-fraction-sep') ?? '|',
                pad:  slider.getAttribute('swiper-fraction-pad') ?? false,
            }
            if (fraction.el) {
                const renderFraction = function (currentClass, totalClass) {
                    return `<span class="${currentClass}"></span><span>${fraction.sep}</span><span class="${totalClass}"></span>`;
                }

                const fractionFormat = (num) => fraction.pad ? `${num}`.padStart(2, '0') : `${num}`;

                if (textPagination == null) {
                    config.pagination = {
                        el: slider.querySelector(this.cfg.selectors.fraction),
                        type: "fraction",
                        renderFraction,
                        formatFractionCurrent: fractionFormat,
                        formatFractionTotal: fractionFormat,
                    }
    
                    modules.push(Pagination);
                } else {
                    config.on ??= {};
                    config.on.init = (swiper) => {
                        const totalSlides = swiper.params.loop ? 
                            swiper.slides.length - swiper.loopedSlides * 2 : 
                            swiper.slides.length;
                        const { currentClass, totalClass } = swiper.params.pagination;
                        
                        const customRender = () => {
                            const activeIndex = swiper.realIndex + 1;
                            fraction.el.innerHTML = renderFraction(currentClass, totalClass);
                            fraction.el.querySelector(`.${currentClass}`).innerText = fractionFormat(activeIndex);
                            fraction.el.querySelector(`.${totalClass}`).innerText = fractionFormat(totalSlides);
                        }

                        customRender();
                        swiper.on('slideChange', () => customRender());
                    }
                }
            }

            const loop = slider.getAttribute('swiper-loop');
            if (typeof loop !== typeof undefined && loop !== null) {
                config.loop = true;
                config.loopedSlides = parseInt(loop);
            }

            const click = slider.getAttribute('swiper-click');
            if (typeof click !== typeof undefined && click !== null) {
                config.slideToClickedSlide = true;
            }

            const nested = slider.getAttribute('swiper-nested');
            if (typeof nested !== typeof undefined && nested !== null) {
                config.nested = true;
            }

            const freeMode = slider.getAttribute('swiper-free-mode');
            if (typeof freeMode !== typeof undefined && freeMode !== null) {
                config.freeMode = true;
            }

            const speed = slider.getAttribute('swiper-speed');
            if (typeof speed !== typeof undefined && speed !== null) {
                config.speed = parseFloat(speed);
            }

            // swiper-desktop-col
            // Set a `swiper-desktop-col` attribute for each `swiper-slide` 
            // element with a value how many cols it should have in desktop version to override the default value.
            // Useful for `Teilabschnittsvorlagen` where overriding via CSS is always difficult.

            const swiperDesktopCols = slider.querySelectorAll('[swiper-desktop-col]');
            if (swiperDesktopCols.length > 0) {
                const value = swiperDesktopCols[0].getAttribute('swiper-desktop-col');
                const desktopColClass = `${this.cfg.classes.hasDesktopColValue}${value}`;
                slider.classList.add(desktopColClass);
            }

            // swiper-multi-row
            const swiperMultiRow = slider.getAttribute('swiper-multi-row');
            if (typeof swiperMultiRow !== typeof undefined && swiperMultiRow !== null) {
                config.slidesPerView = 1;
                config.slidesPerColumn = 2;
                config.slidesPerColumnFill = 'row';
            }

            // -------------------------
            // TODO: Add the following functions if needed.
            // -------------------------

            // const centered = slider.getAttribute('swiper-centered');
            // if (typeof centered !== typeof undefined && centered !== null) {
            //     config.centeredSlides = true;
            // }

            // const centeredBounds = slider.getAttribute('swiper-centered-bounds');
            // if (typeof centeredBounds !== typeof undefined && centeredBounds !== null) {
            //     config.centeredSlides = true;
            //     config.centeredSlidesBounds = true;
            // }

            // fade effect
            const fadeEffect = slider.getAttribute('swiper-fade-effect');
            if (typeof fadeEffect !== typeof undefined && fadeEffect !== null) {
                config.speed = 800;
                config.effect = 'fade';
                config.fadeEffect = {
                    crossFade: true
                },
                config.touchStartPreventDefault = false;
                modules.push(EffectFade);
            }

            // -------------------------
            // Autoplay (PART 1)
            // usage:
            // swiper-autoplay="max-width: 767px"
            // optional:
            // swiper-autoplay-speed: 200 / default: 100 
            // -------------------------

            const autoplay = slider.getAttribute('swiper-autoplay');
            const autoplaySpeed = parseFloat(slider.getAttribute('swiper-autoplay-speed')) || 100; // px / s
            const autoplayDelay = parseFloat(slider.getAttribute('swiper-autoplay-delay')) || 0;

            if (typeof autoplay !== typeof undefined && autoplay !== null) {

                config.autoplay = {
                    delay: autoplayDelay,
                    disableOnInteraction: false,
                },
                config.allowTouchMove = false;
                config.freeMode = true;
                config.autoplaySpeed = autoplaySpeed; 

                this.validateMediaQuery(autoplay);
                modules.push(Autoplay);
            };

            const autoplayProgress = slider.getAttribute('swiper-autoplay-progress');

            if (typeof autoplayProgress !== typeof undefined && autoplayProgress !== null) {

                config.watchSlidesProgress = true;
                config.autoplay = {
                    delay: autoplayDelay,
                    disableOnInteraction: false,
                };

                const getTimeout = (() => {
                    const originalSetTimeout = setTimeout;
                    const timeouts = {};
            
                    setTimeout = (fn, delay) => {
                        const id = originalSetTimeout(fn, delay);
                        timeouts[id] = [Date.now(), delay];
                        return id;
                    };
            
                    return id => {
                        const timeout = timeouts[id];
                        return timeout ? Math.max(timeout[1] - Date.now() + timeout[0], 0) : NaN;
                    };
                })();

                const sanitisePercentage = i => Math.min(100, Math.max(0, i));

                let percentTime = 0;
                let tick;

                const updateProgressBar = (bars, slideDelay) => {
                    const swiper = getSwiperInstance();
                    const startProgressBar = () => {
                        resetProgressBar();
                        tick = setInterval(progress, 1);
                    };

                    function progress() {

                        const timeLeft = getTimeout(swiper.autoplay.timeout);

                        if (swiper.autoplay.running && !swiper.autoplay.paused) {
                            percentTime = sanitisePercentage(100 - timeLeft / slideDelay * 100);
                            bars.forEach(bar => bar.style.width = `${percentTime}%`);

                            if (percentTime > 100) {
                                resetProgressBar();
                            }
                        }

                        if (swiper.autoplay.paused) {
                            percentTime = 0;
                            bars.forEach(bar => bar.style.width = 0);
                        }

                    }

                    const resetProgressBar = () => {
                        percentTime = 0;
                        bars.forEach(bar => bar.style.width = '0%');
                        clearInterval(tick);
                    };

                    startProgressBar();
                }

                config.on = {
                    slideChange: () => {
                        const progressBars = [...slider.querySelectorAll(this.cfg.selectors.progress)];
                        progressBars.length ? updateProgressBar(progressBars, autoplayDelay) : console.warn('Missing progressbar element');
                    },
                }

                this.validateMediaQuery(autoplayProgress);
                modules.push(Autoplay);
            };

            // A11y
            // Add aria-label message
            // Check also "A11y - Custom functions after init" area after the swiper init function for more.
            {
                const attributeMap = {
                    firstSlideMessage: 'Slider.Text.A11yFirstSlide',
                    lastSlideMessage: 'Slider.Text.A11yLastSlide',
                    prevSlideMessage: 'Slider.Text.A11yPrevSlide',
                    nextSlideMessage: 'Slider.Text.A11yNextSlide',
                    paginationBulletMessage: 'Slider.Text.A11yBullet',
                };
    
                config.a11y = Object.fromEntries(
                    Object.entries(attributeMap)
                        .map(([key, textVar]) => [key, window.jsTextVars[textVar]])
                        .filter(([, value]) => value != null)
                );
            }

            modules.push(A11y);

            Swiper.use(modules);
            const getSwiper = () => {
                const containerEl = slider.querySelector(this.cfg.selectors.container);
                const swiper = new Swiper(containerEl, config);

                // Remove current room from other rooms slides before init
                const removeCurrentRoom = slider.getAttribute('swiper-remove-current-room');
                if (typeof removeCurrentRoom !== typeof undefined && removeCurrentRoom !== null) {
                    const pageSubIndex = slider.dataset.subIndex;
                    const slides = [...slider.querySelectorAll(this.cfg.selectors.slide)];
                    swiper.on('beforeInit', () => {
                        slides.find(slide => slide.dataset.roomCategoryId === pageSubIndex).remove();
                    });
                }
    
                // Remove current room from room slider before init
                const removeRoomSlider = slider.getAttribute('swiper-remove-room-slider');
                if (typeof removeRoomSlider !== typeof undefined && removeRoomSlider !== null) {
                    const roomSlides = [...slider.querySelectorAll(this.cfg.selectors.slide)];
    
                    swiper.on('beforeInit', () => {
                        const slideToRemove = roomSlides.find(slide => slide.dataset.currentRoomIndex === slide.dataset.sliderRoomIndex);
                        if (slideToRemove) {
                            slideToRemove.remove();
                        }
                    });
                }

                for (const [el, label] of [
                    [config?.pagination?.el, 'pagination'],
                    [config?.navigation?.prevEl, 'navPrev'],
                    [config?.navigation?.nextEl, 'navNext']
                ]) {
                    if (!el) { continue; }
                    el.addEventListener('click', () => {
                        swiper.target = label;
                    }, false);
                }
    
                // -------------------------
                // Autoplay (PART 2 - afterinit start-stop function)
                // -------------------------
    
                if (typeof autoplay !== typeof undefined && autoplay !== null) {
                    const mql = window.matchMedia(`(${autoplay})`);
    
                    function setSpeed() {
                        swiper.params.speed = swiper.slides[0].offsetWidth / config.autoplaySpeed * 1000; // s to ms
                    }
    
                    swiper.on('afterInit', () => {
                        function start() {
                            setSpeed();
                            swiper.autoplay.start();
                        }
    
                        function stop() {
                            swiper.autoplay.stop();
                            swiper.params.speed = 300;
                        }
    
                        mql.matches ? start() : stop();
    
                        mql.addEventListener('change', (e) => {
                            e.matches ? start() : stop();
                        });
                    });
    
                    swiper.on('resize', () => {
                        if (mql.matches) {
                            setSpeed();
                        }
                    });
                }

                const destroySliderIfSingle = slider.getAttribute('swiper-destroy-if-single');
                if (typeof destroySliderIfSingle !== typeof undefined && destroySliderIfSingle !== null) {                    
                    const realSlides = slider.querySelectorAll('.swiper-slide:not(.swiper-slide-duplicate)');
                    
                    swiper.on('afterInit', () => {
                        if (realSlides.length <= 1 && swiper.initialized) {

                            const controls = slider.querySelector(this.cfg.selectors.controls.container);
                            if (controls) controls.classList.add(this.cfg.classes.isHidden);
                            swiper.destroy(true, true);
                        }
                    });
                };

                // A11y - Use focus to navigate the slider
                const focusFix = slider.querySelector(this.cfg.selectors.focusFix) ?? slider.className.includes(this.cfg.selectors.focusFix.replace('.', '')) ? slider : null;
                swiper.on('afterInit', () => {
                    swiper.wrapperEl.addEventListener('focusin', (e) => {
                        focusFix && focusFix.scrollTo(0, 0); // prevent focus from moving the entire slider container
                        swiper.updateClickedSlide(e);
                        swiper.slideToClickedSlide();
                    });

                    for (const slide of swiper.slides) {
                        if (slide.classList.contains('swiper-slide-duplicate')) {
                            slide.inert = true;
                        }
                    }
                });
    
                // controls by index of slides
                // const indexControls = slider.getAttribute('swiper-index-control');
                // const elementRoot = slider.closest('[swiper-root]');
                // if (typeof indexControls !== typeof undefined && indexControls !== null && typeof elementRoot !== typeof undefined && elementRoot !== null ) {
                //     let controls = [...elementRoot.querySelectorAll(this.cfg.selectors.indexControl)];
    
                //     controls.forEach((control, i) => {
                //         control.addEventListener('click', () => {
                //             swiper.slideTo(i);
                //         })
                //     });
    
                //     swiper.on('slideChange', () => {
                //         //controls.map(item => item.removeAttribute('checked'));
                //         controls[swiper.activeIndex].querySelector('label').click();
                //     });
                // }
    
                // If last image of the loop is not loading, because part of the image is hidden use this function
                // const lazyFix = slider.getAttribute('swiper-lazy-fix');
                // if (typeof lazyFix !== typeof undefined && lazyFix !== null) {
                //     swiper.on('afterInit', () => {
                //         swiper.slideToLoop(1);
                //         swiper.slideToLoop(0);
                //     });
                // }
    
                return swiper;
            }

            // This snippet validates the presence of the "swiper-enabled" attribute on the 
            // swiper and ensures it contains a valid value for the media query. 
            // Swiper is then configured to be activated or deactivated according to this media query, 
            // providing warnings for any invalid values.
            // usage: swiper-enabled="max-width: 767px"

            const enabled = slider.getAttribute('swiper-enabled');
            let init = true;
            if (typeof enabled !== typeof undefined && enabled !== null) {

                if (enabled.trim() === '') return console.warn(`swiper-enabled is empty`);

                this.validateMediaQuery(enabled);
                const mql = window.matchMedia(`(${enabled})`);
                init = mql.matches;

                mql.addEventListener('change', (e) => {
                    e.matches ? getSwiper().init() : getSwiperInstance().destroy(true, true);
                });
            };
            
            if (init) {
                const swiper = getSwiper();
                swiper.init();
                window.swiper.push(swiper);
            }
        });
    },

    init() {
        console.debug(`Init: ${this.cfg.name}`);
        this.setElements();

        if (this.cfg.el.$sliders) {
            this.initSliders();
        }
    },
};

export default slider;